import { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PropTypes from 'prop-types';
import QRCode from 'react-qr-code';
import i18n from '../../../common/i18n';
import Reveal from '../commons/Reveal';
import SectionHeading from '../commons/SectionHeading';
import { APP_DEMO_SCREENS, APP_DEMO_STEPS, getDownloadAppLink } from '../commons/homeData';
import { redirectToStore } from '../../../../utils/functionUtils';
import appMonto470 from '../../../../assests/images/home/redesign/appMonto470.png';
import {
  bodyFont, bodyTextSx, cardShadow, ctaSx, homeColors, homeEase, sectionContentSx, sectionPadding,
  smallTextSx,
} from '../style/homeStyles';

// Paso en el que se resalta el saldo disponible dentro de la demo.
const SALDO_STEP = 2;
const TAB_HIGHLIGHT_FROM_STEP = 1;
const DETAIL_HIGHLIGHT_STEP = 3;

// Lado del QR de desktop, en px.
const QR_SIZE = 132;

const sparkBaseSx = {
  position: 'absolute',
  width: 7,
  height: 7,
  borderRadius: '999px',
  opacity: 0,
  animation: 'homeSpark 1.5s ease-in-out infinite',
};

const AppSection = ({ isMobile }) => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const timeout = setTimeout(
      () => setStep((current) => (current + 1) % APP_DEMO_STEPS.length),
      APP_DEMO_STEPS[step].duration,
    );
    return () => clearTimeout(timeout);
  }, [step]);

  const currentStep = APP_DEMO_STEPS[step];
  const { screen: screenIndex, finger } = currentStep;
  const saldoVisible = step === SALDO_STEP;

  const heading = (
    <SectionHeading
      eyebrow={i18n.homeAppEyebrow}
      title={i18n.homeAppTitle}
      titleFontSize={isMobile ? 28 : 48}
      mb={26}
    />
  );

  const caption = (
    <Reveal delay={80}>
      <Typography
        component="p"
        sx={{
          textAlign: 'center',
          marginBottom: '14px',
          minHeight: 18,
          fontFamily: bodyFont,
          fontWeight: 500,
          fontSize: 13,
          letterSpacing: '0.01em',
          color: homeColors.ink3,
          transition: `opacity .35s ${homeEase}`,
        }}
      >
        {currentStep.caption}
      </Typography>
    </Reveal>
  );

  const description = (
    <Reveal delay={80}>
      <Typography
        component="p"
        sx={{
          ...bodyTextSx,
          marginTop: '-8px',
          marginBottom: isMobile ? '18px' : 0,
          maxWidth: 460,
          fontSize: isMobile ? 15 : 20,
        }}
      >
        {i18n.homeAppDescription}
      </Typography>
    </Reveal>
  );

  const phone = (
    <Reveal delay={120}>
      <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
        {/* Resplandor magenta detrás del teléfono. */}
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            width: 280,
            height: 280,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(218,0,103,0.22) 0%, rgba(218,0,103,0) 70%)',
            top: 50,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 0,
          }}
        />

        <Box sx={{
          position: 'relative',
          zIndex: 1,
          width: 232,
          height: 452,
          borderRadius: '38px',
          backgroundColor: homeColors.ink,
          padding: '7px',
          boxShadow: `0 36px 60px -22px rgba(31,27,29,0.45),
            0 0 0 1px rgba(31,27,29,0.5),
            inset 0 0 0 1.5px rgba(255,255,255,0.06)`,
        }}
        >
          <Box sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            borderRadius: '30px',
            backgroundColor: homeColors.white,
            overflow: 'hidden',
          }}
          >
            {APP_DEMO_SCREENS.map((screen, index) => (
              <Box
                key={screen}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  opacity: screenIndex === index ? 1 : 0,
                  transition: `opacity .8s ${homeEase}`,
                  pointerEvents: 'none',
                }}
              >
                <Box
                  component="img"
                  src={screen}
                  alt=""
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    objectPosition: 'top center',
                    backgroundColor: homeColors.white,
                  }}
                />
              </Box>
            ))}

            {/* Resalte del tab "Línea de crédito". */}
            <Box
              aria-hidden="true"
              sx={{
                position: 'absolute',
                pointerEvents: 'none',
                zIndex: 2,
                left: '3%',
                top: '28.7%',
                width: '46%',
                height: '6.4%',
                borderRadius: '999px',
                boxShadow: `0 0 0 2.5px ${homeColors.pink}, 0 6px 18px -4px rgba(218,0,103,0.45)`,
                opacity: (screenIndex === 0 && step >= TAB_HIGHLIGHT_FROM_STEP) ? 1 : 0,
                transition: `opacity .5s ${homeEase}`,
              }}
            />

            {/* Revelado del saldo disponible. */}
            <Box
              aria-hidden="true"
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 3,
                pointerEvents: 'none',
                opacity: saldoVisible ? 1 : 0,
                transition: `opacity .45s ${homeEase}`,
                '@keyframes homeSaldoGlow': {
                  '0%, 100%': { transform: 'translate(-50%, -50%) scale(0.9)', opacity: 0.7 },
                  '50%': { transform: 'translate(-50%, -50%) scale(1.1)', opacity: 1 },
                },
                '@keyframes homeSaldoPop': {
                  '0%': { transform: 'scale(0.92) translateY(5px)' },
                  '55%': { transform: 'scale(1.16) translateY(-2px)' },
                  '100%': { transform: 'scale(1.1) translateY(0)' },
                },
                '@keyframes homeChipRise': {
                  '0%': { opacity: 0, transform: 'translate(-50%, -30%) scale(0.8)' },
                  '100%': { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' },
                },
                '@keyframes homeSpark': {
                  '0%, 100%': { opacity: 0, transform: 'scale(0.3)' },
                  '50%': { opacity: 1, transform: 'scale(1)' },
                },
              }}
            >
              <Box sx={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.58)' }} />
              <Box sx={{
                position: 'absolute',
                left: '30%',
                top: '49.5%',
                width: 170,
                height: 170,
                transform: 'translate(-50%, -50%)',
                borderRadius: '999px',
                background: 'radial-gradient(circle, rgba(218,0,103,0.30) 0%, rgba(218,0,103,0) 70%)',
                animation: 'homeSaldoGlow 1.8s ease-in-out infinite',
              }}
              />
              <Box
                component="img"
                src={appMonto470}
                alt=""
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  objectPosition: 'top center',
                  transformOrigin: '29% 49.5%',
                  animation: saldoVisible ? `homeSaldoPop 1.3s ${homeEase} forwards` : 'none',
                }}
              />
              <Typography
                component="span"
                sx={{
                  position: 'absolute',
                  left: '29%',
                  top: '41%',
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: homeColors.pink,
                  color: homeColors.white,
                  fontFamily: bodyFont,
                  fontWeight: 600,
                  fontSize: 9,
                  letterSpacing: '0.04em',
                  padding: '4px 10px',
                  borderRadius: '999px',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 6px 14px -4px rgba(218,0,103,0.5)',
                  opacity: 0,
                  animation: saldoVisible ? `homeChipRise 1.3s ${homeEase} .25s forwards` : 'none',
                }}
              >
                {i18n.homeAppAvailableCredit}
              </Typography>
              <Box sx={{
                ...sparkBaseSx,
                left: '49%',
                top: '44.5%',
                backgroundColor: homeColors.yellow,
                animationDelay: '.2s',
              }}
              />
              <Box sx={{
                ...sparkBaseSx, left: '20%', top: '53%', backgroundColor: homeColors.pink, animationDelay: '.6s',
              }}
              />
              <Box sx={{
                ...sparkBaseSx, left: '52%', top: '52.5%', backgroundColor: homeColors.yellow, animationDelay: '1s',
              }}
              />
            </Box>

            {/* Resalte de "Ver detalle". */}
            <Box
              aria-hidden="true"
              sx={{
                position: 'absolute',
                pointerEvents: 'none',
                zIndex: 2,
                right: '4%',
                top: '36.9%',
                width: '30%',
                height: '4.6%',
                borderRadius: '999px',
                boxShadow: `0 0 0 2.5px ${homeColors.pink}, 0 6px 16px -4px rgba(218,0,103,0.4)`,
                opacity: (screenIndex === 1 && step === DETAIL_HIGHLIGHT_STEP) ? 1 : 0,
                transition: `opacity .5s ${homeEase}`,
              }}
            />

            {/* Dedo que simula el toque. */}
            <Box
              aria-hidden="true"
              sx={{
                position: 'absolute',
                zIndex: 5,
                width: 30,
                height: 30,
                pointerEvents: 'none',
                left: `${finger.x}%`,
                top: `${finger.y}%`,
                opacity: finger.visible ? 1 : 0,
                transform: `translate(-50%,-50%) scale(${finger.pressed ? 0.82 : 1})`,
                transition: `left .65s cubic-bezier(.5,0,.2,1), top .65s cubic-bezier(.5,0,.2,1),
                  transform .2s ${homeEase}, opacity .4s ${homeEase}`,
                '@keyframes homeFingerRing': {
                  '0%': { transform: 'scale(0.7)', opacity: 0.8 },
                  '100%': { transform: 'scale(1.5)', opacity: 0 },
                },
              }}
            >
              <Box sx={{
                position: 'absolute',
                inset: '-8px',
                borderRadius: '999px',
                border: '2px solid rgba(218,0,103,0.6)',
                opacity: finger.pressed ? 1 : 0,
                transition: `opacity .25s ${homeEase}`,
                animation: 'homeFingerRing 1s ease-out infinite',
              }}
              />
              <Box sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: '999px',
                backgroundColor: 'rgba(31,27,29,0.32)',
                border: '2px solid rgba(255,255,255,0.85)',
                boxShadow: '0 4px 10px rgba(31,27,29,0.30)',
              }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Reveal>
  );

  // Mobile: botón que abre la tienda del sistema operativo del propio dispositivo.
  const cta = (
    <Reveal delay={300}>
      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '26px' }}>
        <Button
          onClick={redirectToStore}
          endIcon={<FileDownloadOutlinedIcon />}
          sx={{
            ...ctaSx,
            fontFamily: bodyFont,
            width: '90%',
            // El Button de MUI le gana en especificidad a un sx puesto en el ícono.
            '& .MuiButton-endIcon > *:nth-of-type(1)': { fontSize: 24 },
          }}
        >
          {i18n.homeAppCta}
        </Button>
      </Box>
    </Reveal>
  );

  // Desktop: en vez del botón, un QR. Codifica la landing /descargar-app (no la tienda) para que
  // sea esa página la que mande a Play Store o App Store según el celular que escaneó.
  const qr = (
    <Reveal delay={300}>
      <Box sx={{ marginTop: '26px' }}>
        <Typography component="p" sx={{ ...smallTextSx, marginBottom: '14px' }}>
          {i18n.homeAppQrCaption}
        </Typography>
        {/* El padding es el "quiet zone" que los lectores necesitan alrededor del código. */}
        <Box sx={{
          display: 'inline-flex',
          padding: '14px',
          backgroundColor: homeColors.white,
          border: `1px solid ${homeColors.line}`,
          borderRadius: '16px',
          boxShadow: cardShadow,
        }}
        >
          <QRCode
            value={getDownloadAppLink()}
            size={QR_SIZE}
            fgColor={homeColors.ink}
            bgColor={homeColors.white}
            title={i18n.homeAppQrCaption}
            style={{ display: 'block', height: QR_SIZE, width: QR_SIZE }}
          />
        </Box>
      </Box>
    </Reveal>
  );

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: homeColors.white,
        padding: sectionPadding(isMobile),
        textAlign: 'left',
      }}
    >
      <Box sx={sectionContentSx}>
        {/* Mobile: todo apilado. Desktop: la demo a la izquierda y el texto con el CTA a la derecha. */}
        {isMobile ? (
          <>
            {heading}
            {description}
            {caption}
            {phone}
            {cta}
          </>
        ) : (
          // Grid propio y no el de MUI: su spacing mete un padding-left que desalinea el contenido.
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: '5fr 7fr',
            gap: '48px',
            alignItems: 'center',
          }}
          >
            <Box>
              {caption}
              {phone}
            </Box>
            <Box>
              {heading}
              {description}
              {qr}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

AppSection.propTypes = {
  isMobile: PropTypes.bool,
};

AppSection.defaultProps = {
  isMobile: false,
};

export default AppSection;
